import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";

const EconomicsScalingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      step: "01",
      title: "Elasticity Without Costs",
      description:
        "A human dispatcher can process one request at a time. The digital employee handles 50 parallel dialogues without losing a single thread of context. Whether you get 5 inquiries or 500, it intercepts every single one in under 9 seconds.",
    },
    {
      step: "02",
      title: "Build Institutional Memory",
      description:
        "When your top performer leaves, they take their negotiation skills with them. Stop renting employee skills. Train your digital employee once, and your perfect sales protocol is permanently hardcoded into the foundation of your business as an inalienable digital asset.",
    },
    {
      step: "03",
      title: "Decouple Growth from Payroll",
      description:
        "Scale to up to 2x more qualified appointments on the same ad spend—while keeping your headcount frozen.",
    },
  ];

  return (
    <section className="section-padding bg-[#050810] relative overflow-hidden" data-testid="economics-scaling-section" ref={ref}>
      <div className="absolute inset-0 bg-gradient-to-bl from-indigo-500/[0.04] via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            className="relative order-2 lg:order-1"
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-indigo-500/50 via-indigo-500/20 to-indigo-500/50 hidden sm:block" />

            <div className="space-y-6">
              {pillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  className="relative flex gap-4 sm:gap-6"
                  variants={fadeUpVariants}
                  data-testid={`economics-scaling-pillar-${index}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
                    <span className="text-indigo-400 font-mono text-xs font-bold">{pillar.step}</span>
                  </div>

                  <div className="flex-1 min-w-0 bento-card p-5 sm:p-7 border border-white/[0.06] bg-white/[0.02] hover:border-indigo-500/20 transition-colors duration-300">
                    <h3 className="text-lg sm:text-xl font-bold mb-2 text-white">{pillar.title}</h3>
                    <p className="text-gray-400 font-body leading-relaxed text-sm sm:text-base">{pillar.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <ChoiceRevealOnScroll className="order-1 lg:order-2 lg:sticky lg:top-28 lg:text-right">
            <span className="badge-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-full inline-block mb-6">
              THE ECONOMICS OF SCALING
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight text-left lg:text-right" data-testid="economics-scaling-headline">
              Zero-Liability Scaling:
              <br />
              The <span className="gradient-text">Math of 2x</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-400 font-body leading-relaxed text-left lg:text-right max-w-xl lg:ml-auto" data-testid="economics-scaling-intro">
              Traditional scaling means hiring more people—increasing taxes, sick days, burnout, and management liabilities. A digital employee breaks this{" "}
              <span className="text-indigo-400 font-semibold">linear trap</span>: same ad spend,{" "}
              <span className="text-indigo-400 font-semibold">zero new hires</span>, up to{" "}
              <span className="text-indigo-400 font-semibold">2x more qualified appointments</span>.
            </p>

            <div className="mt-8 flex lg:justify-end items-center gap-3 text-xs font-mono text-gray-500">
              <span className="w-2 h-2 rounded-full bg-indigo-400 pulse-subtle flex-shrink-0" />
              <span className="text-left lg:text-right">PAYROLL LINEAR → SAME AD SPEND → 2x QUALIFIED</span>
            </div>
          </ChoiceRevealOnScroll>
        </div>
      </div>
    </section>
  );
};

export default EconomicsScalingSection;
